import { h, Fragment } from "preact";
import { useState, useMemo, useEffect } from "preact/hooks";
import unwrapNodes from "../../helpers/unwrapNodes";
import {
  storefrontCart,
  cartDeliveryAddressesUpdate,
  cartDeliveryAddressesAdd,
  cartWithDeliveryGroups,
} from "../../global/storefront/queries";
import { createStorefrontApiClient } from "@shopify/storefront-api-client";
import ShippingMethodOptions from "./shipping-method-options";

const client = createStorefrontApiClient({
  storeDomain: `https://${window.api.shop}`,
  apiVersion: window.api.version,
  publicAccessToken: window.api.token,
});

const getAddressHash = (address) => {
  if (!address) return null;

  const keys = ["address1", "city", "provinceCode", "zip", "countryCode", "id"];

  return keys.map((key) => address[key]).join();
};

const equivalentAddress = (address1, address2) => {
  if (!address1 || !address2) return false;

  return getAddressHash(address1) === getAddressHash(address2);
};

export default function ShippingMethods({
  deliveryOptionHandle,
  setDeliveryOptionHandle,
  shippingAddress,
  storefrontCartId,
}) {
  const [deliveryGroups, setDeliveryGroups] = useState([]);
  const [error, setError] = useState(null);
  const shippingAddressId = shippingAddress?.id;
  const addressHash = getAddressHash(shippingAddress);

  useEffect(() => {
    if (!shippingAddressId) return;

    setDeliveryGroups([]);
    setError(null);

    const getMethods = async () => {
      const { data } = unwrapNodes(
        await client.request(storefrontCart, {
          variables: {
            id: storefrontCartId,
          },
        }),
      );

      const cartShippingAddress = data.cart.delivery.addresses.find(
        ({ address }) => equivalentAddress(address, shippingAddress),
      );

      if (cartShippingAddress) {
        if (!cartShippingAddress.selected) {
          const { data } = unwrapNodes(
            await client.request(cartDeliveryAddressesUpdate, {
              variables: {
                id: storefrontCartId,
                addresses: [
                  {
                    id: cartShippingAddress.id,
                    selected: true,
                  },
                ],
              },
            }),
          );
        }
      } else {
        const { address1, city, countryCode, provinceCode, zip } =
          shippingAddress;

        const addressInput = {
          selected: true,
          oneTimeUse: true,
          address: {
            deliveryAddress: {
              address1,
              city,
              countryCode,
              provinceCode,
              zip,
            },
          },
        };

        const { data } = unwrapNodes(
          await client.request(cartDeliveryAddressesAdd, {
            variables: {
              id: storefrontCartId,
              addresses: [addressInput],
            },
          }),
        );
      }

      const cartWithDeliveryGroupsStream = await client.requestStream(
        cartWithDeliveryGroups,
        {
          variables: { id: storefrontCartId },
        },
      );

      let cartWithDeliveryGroupsData = null;

      for await (const response of cartWithDeliveryGroupsStream) {
        const { data, errors, extensions, hasNext } = response;
        cartWithDeliveryGroupsData = unwrapNodes(data);
      }

      if (cartWithDeliveryGroupsData.cart.deliveryGroups.length) {
        setDeliveryGroups(cartWithDeliveryGroupsData.cart.deliveryGroups);
      } else {
        setError(window.localeStrings.save_quote.no_methods_error);
      }
    };

    getMethods();
  }, [addressHash]);

  let content = null;

  if (!shippingAddress) {
    content = <div>{window.localeStrings.save_quote.select_address}</div>;
  } else if (error) {
    content = <div>{error}</div>;
  } else if (deliveryGroups.length) {
    content = (
      <>
        {/* Draft orders only support one shipping method so ignore any additional groups */}
        {deliveryGroups.slice(0, 1).map((deliveryGroup, i) => (
          <ShippingMethodOptions
            deliveryGroup={deliveryGroup}
            key={deliveryGroup.id}
            i={i}
            deliveryOptionHandle={deliveryOptionHandle}
            setDeliveryOptionHandle={setDeliveryOptionHandle}
          />
        ))}
      </>
    );
  } else {
    content = (
      <div className="save-quote__shipping-method-options">
        <span className="visually-hidden">
          {window.localeStrings.save_quote.loading}
        </span>
        <div className="save-quote__shipping-methods-skeleton-item"></div>
        <div className="save-quote__shipping-methods-skeleton-item"></div>
        <div className="save-quote__shipping-methods-skeleton-item"></div>
      </div>
    );
  }

  return (
    <div className="save-quote__shipping-methods">
      <div className="save-quote__shipping-methods-title">
        {window.localeStrings.save_quote.shipping_method}
      </div>
      {content}
    </div>
  );
}
